import React from "react";
import { Link } from "react-router";
import {
  ArrowRight,
  Building2,
  Car,
  CheckCircle2,
  FileText,
  Flag,
  HeartPulse,
  Shield,
  Sparkles,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  LegalTopicIntroHero,
  MediaCarousel,
  PodcastCard,
  SectionHead,
  VideoCard,
  foregroundOnSolidAccent,
} from "../shared/categoryDetailShared";

const ListBlock = ({ items, color, icon: Icon = CheckCircle2 }) => (
  <ul className="space-y-2.5">
    {items.map((line) => (
      <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-gray-800 sm:text-[0.9375rem]">
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: `${color}14`, color }}
        >
          <Icon size={14} strokeWidth={2.25} />
        </span>
        <span className="min-w-0 pt-0.5">{line}</span>
      </li>
    ))}
  </ul>
);

/** Overview — Personal injury & accident claims (NIC, Motor Compensation Fund, civil courts). */
export const PersonalInjuryClaimsOverviewTab = ({ data, color }) => (
  <div className="space-y-10">
    <LegalTopicIntroHero
      color={color}
      categoryEyebrow="LEGAL · PERSONAL INJURY &amp; ACCIDENT CLAIMS (GHANA)"
      funnelTagline={data.funnelTagline}
      FunnelIcon={Shield}
      gallerySrc={data.galleryImages[0]}
      imageBandIcon={FileText}
      imageBandLabel="Evidence · NIC · courts"
      imageSubtitle="Claim early—prove everything—in writing."
      statsPreview={data.stats}
    >
      <h2 className="max-w-2xl text-[1.6rem] font-black leading-[1.14] tracking-[-0.02em] text-slate-950 sm:text-[1.95rem] lg:text-[2.125rem]">
        {data.introduction.headline}
      </h2>
      <p className="mt-5 max-w-prose text-base leading-relaxed text-slate-600 sm:text-[1.05rem] sm:leading-relaxed">{data.introduction.lead}</p>

      <div
        className="mt-5 rounded-2xl border border-slate-200/95 bg-white/85 px-5 py-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.9)] backdrop-blur-sm sm:px-6 sm:py-5"
        style={{ borderLeftWidth: "4px", borderLeftColor: color }}
      >
        <p className="text-sm font-semibold leading-relaxed text-slate-800 sm:text-[0.9375rem]">{data.introduction.problemStarts}</p>
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200/85 bg-white/95 p-5 shadow-[0_22px_50px_-40px_rgba(15,23,42,0.35)] sm:p-7">
        <p className="text-[11px] font-black uppercase tracking-[0.26em] text-slate-500">{data.introduction.frameworkLead}</p>
        <ul className="mt-4 space-y-3 border-t border-slate-100/90 pt-4">
          {data.introduction.frameworkBullets.map((f) => (
            <li key={f} className="flex gap-3 text-sm font-semibold leading-relaxed text-slate-800 sm:text-[0.9375rem]">
              <span
                className="mt-[0.35rem] inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: color, boxShadow: `0 0 0 3px ${color}22` }}
                aria-hidden
              />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {data.focusAreas.map((tag) => (
          <span
            key={tag}
            className="rounded-xl border border-slate-200/90 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 shadow-sm transition hover:border-slate-300/95"
          >
            {tag}
          </span>
        ))}
      </div>
    </LegalTopicIntroHero>

    <section>
      <SectionHead eyebrow="High intent" title={data.miniSiteGoals.title} subtitle={data.miniSiteGoals.subtitle} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <div className="grid gap-2 md:grid-cols-2">
          {data.miniSiteGoals.questions.map((q) => (
            <div key={q} className="rounded-lg border border-slate-100 bg-slate-50/80 px-4 py-3 text-sm font-semibold text-slate-900">
              • {q}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Coverage" title={data.scopeSection.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <div className="grid gap-2 sm:grid-cols-2">
          {data.scopeSection.items.map((item) => (
            <div key={item} className="rounded-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900">
              • {item}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Foundation" title={data.firstPrinciple.title} color={color} />
        <p className="-mt-2 mb-6 text-sm font-semibold leading-relaxed text-slate-700">{data.firstPrinciple.lead}</p>
        <div className="rounded-xl border border-emerald-200/75 bg-emerald-50/40 p-5">
          <p className="text-sm font-semibold text-emerald-950">{data.firstPrinciple.nicNote}</p>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.documentsChecklist.title}</p>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {data.documentsChecklist.items.map((doc) => (
            <li key={doc} className="rounded-lg border border-slate-100 bg-slate-50/90 px-3 py-2 text-xs font-semibold text-slate-800 sm:text-[13px]">
              • {doc}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs font-semibold leading-relaxed text-slate-700 sm:text-sm">{data.documentsChecklist.nicMotorNote}</p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Motor" title={data.roadAccidents.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="max-w-prose text-sm font-semibold leading-relaxed text-slate-800">{data.roadAccidents.body}</p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="First response" title={data.afterAccident.title} subtitle="Step-by-step" color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <ol className="list-decimal space-y-2 pl-5 text-sm font-semibold text-slate-900">
          {data.afterAccident.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Loss of life" title={data.deathClaims.title} color={color} />
        <p className="-mt-2 text-sm font-semibold leading-relaxed text-slate-700">{data.deathClaims.body}</p>
      </div>
      <div className="rounded-xl border border-indigo-200/70 bg-indigo-50/40 p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-indigo-950">{data.lifeInsurance.title}</p>
        <p className="mt-3 text-sm font-semibold text-indigo-950/95">{data.lifeInsurance.lead}</p>
        <ul className="mt-4 space-y-1 text-sm font-semibold text-indigo-950/95">
          {data.lifeInsurance.checklist.map((c) => (
            <li key={c}>• {c}</li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Tort / contract" title={data.personalInjuryComp.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="max-w-prose text-sm font-semibold leading-relaxed text-slate-800">{data.personalInjuryComp.body}</p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Buildings & stock" title={data.propertyClaims.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="mb-4 text-sm font-semibold text-slate-700">{data.propertyClaims.lead}</p>
        <ListBlock items={data.propertyClaims.items} color={color} icon={Building2} />
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-orange-200/70 bg-orange-50/35 p-6 sm:p-8">
        <p className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-orange-950">{data.fireClaims.title}</p>
        <ul className="mt-4 space-y-2 text-sm font-semibold text-orange-950/95">
          {data.fireClaims.tips.map((t) => (
            <li key={t}>• {t}</li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.theftClaims.title}</p>
        <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-800">
          {data.theftClaims.tips.map((t) => (
            <li key={t}>• {t}</li>
          ))}
        </ul>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Friction" title={data.disputes.title} color={color} />
        <p className="-mt-2 text-sm font-semibold leading-relaxed text-slate-700">{data.disputes.body}</p>
      </div>
      <div className="rounded-xl border border-slate-900/[0.08] bg-slate-950 p-6 text-white sm:p-8">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          NIC
        </p>
        <h3 className="mt-2 text-lg font-black sm:text-xl">{data.escalateNic.title}</h3>
        <ListBlock items={data.escalateNic.items} color={color} icon={Car} />
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Litigation" title={data.courtAction.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="max-w-prose text-sm font-semibold leading-relaxed text-slate-800">{data.courtAction.body}</p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Reality" title={data.commonProblems.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <ul className="grid gap-2 sm:grid-cols-2">
          {data.commonProblems.items.map((i) => (
            <li key={i} className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900">
              • {i}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Playbook" title={data.practicalTips.title} color={color} />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[data.practicalTips.afterAnyIncident, data.practicalTips.beforeSettlement, data.practicalTips.fireBurglary, data.practicalTips.lifeClaim].map((block) => (
          <div key={block.heading} className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {block.heading}
            </p>
            <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-700">
              {block.items.map((it) => (
                <li key={it}>• {it}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Warnings" title={data.redFlags.title} color={color} />
      <div className="rounded-xl border border-red-200/70 bg-red-50/35 p-6 sm:p-8">
        <ul className="space-y-3">
          {data.redFlags.items.map((r) => (
            <li key={r} className="flex gap-3 text-sm font-semibold text-red-950">
              <Flag size={16} className="mt-0.5 shrink-0 text-red-500" aria-hidden />
              {r}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Content funnel" title={data.editorialPlaybook.title} color={color} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[
          { label: "Road accidents", items: data.editorialPlaybook.road },
          { label: "Personal injury", items: data.editorialPlaybook.injury },
          { label: "Life insurance", items: data.editorialPlaybook.life },
          { label: "Property claims", items: data.editorialPlaybook.property },
          { label: "Disputes", items: data.editorialPlaybook.disputes },
        ].map((col) => (
          <div key={col.label} className="rounded-xl border border-slate-200/90 bg-white p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{col.label}</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {col.items.map((item) => (
                <li key={item}>• {item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Funnel" title={data.funnelApplication.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.funnelApplication.hooksHeading}</p>
        <ul className="mt-3 space-y-2">
          {data.funnelApplication.hooks.map((h) => (
            <li key={h} className="text-sm font-bold text-slate-900">
              • {h}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {[data.funnelApplication.education, data.funnelApplication.authority, data.funnelApplication.conversion].map((step) => (
          <div key={step.label} className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color }}>
              {step.label}
            </p>
            <p className="mt-3 text-sm font-bold leading-relaxed text-slate-900">{step.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-slate-950 p-8 text-white sm:p-10">
      <div
        className="absolute inset-0 opacity-30 blur-3xl"
        style={{ background: `radial-gradient(circle at 30% 20%, ${color}, transparent 55%)` }}
        aria-hidden
      />
      <div className="relative">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.finalTakeaway.title}
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {data.finalTakeaway.pillars.map((p) => (
            <span key={p} className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-black text-white/95">
              {p}
            </span>
          ))}
        </div>
        <p className="mt-8 text-xs font-black uppercase tracking-wider text-white/50">{data.finalTakeaway.messageLead}</p>
        <ul className="mt-3 space-y-2 text-sm font-semibold text-white/85">
          {data.finalTakeaway.mistakes.map((m) => (
            <li key={m}>• {m}</li>
          ))}
        </ul>
        <p className="mt-8 flex items-start gap-2 text-base font-black leading-snug text-white">
          <HeartPulse size={22} className="mt-1 shrink-0 opacity-90" style={{ color }} aria-hidden />
          {data.finalTakeaway.safeMessage}
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Summary" title={data.bottomLine.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-8 text-center shadow-sm">
        <p className="text-lg font-black text-slate-950">{data.bottomLine.lead}</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
          <p className="rounded-lg border border-emerald-200/80 bg-emerald-50/50 p-4 text-sm font-bold text-emerald-950">{data.bottomLine.organised}</p>
          <span className="text-sm font-black text-slate-400">{data.bottomLine.vs}</span>
          <p className="rounded-lg border border-orange-200/80 bg-orange-50/55 p-4 text-sm font-bold text-orange-950">{data.bottomLine.chaotic}</p>
        </div>
        <p className="mt-6 inline-flex flex-wrap items-center justify-center gap-2 text-sm font-semibold text-slate-600">
          <ArrowRight size={16} style={{ color }} aria-hidden />
          {data.bottomLine.closing}
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Frequently Asked" title="Most-Asked Questions" color={color} />
      <div className="space-y-2">
        {data.faqs.map((faq) => (
          <FAQItem key={faq.q} q={faq.q} a={faq.a} color={color} />
        ))}
      </div>
    </section>
  </div>
);

export const PersonalInjuryClaimsArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-10">
    <section>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <SectionHead eyebrow="Library" title="Articles &amp; Guides" subtitle={data.funnelTagline} color={color} />
        <span className="mb-8 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {data.articles.length} articles
        </span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section
      className="overflow-hidden rounded-xl p-8 sm:p-10"
      style={{
        background: `linear-gradient(128deg, ${color}12, transparent)`,
        border: `1px solid ${color}35`,
      }}
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.editorialPlaybook.leadCtasHeading}
          </p>
          <h3 className="text-xl font-black text-slate-950 sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-600">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl px-7 py-4 text-sm font-black shadow-md transition hover:scale-[1.02]"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register Free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const PersonalInjuryClaimsMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHead
          eyebrow="Watch &amp; listen"
          title="Videos &amp; podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
          color={color}
        />
        <span className="mt-1 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {total} featured
        </span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="pi-media-videos-heading" className="min-w-0">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Personal injury and accident claim videos"
            items={videos}
            header={<h3 id="pi-media-videos-heading" className="text-lg font-black text-slate-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="pi-media-podcasts-heading" className="min-w-0">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Personal injury and insurance podcast episodes"
            items={podcasts}
            header={<h3 id="pi-media-podcasts-heading" className="text-lg font-black text-slate-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
          <p className="mt-4 px-2 text-center text-xs leading-relaxed text-gray-400 sm:text-left sm:text-sm md:mt-6">
            Listen links appear here when Spotify, Apple Podcasts, or other feeds are connected from admin or your CMS.
          </p>
        </section>
      ) : null}

      <div className="overflow-hidden rounded-xl bg-slate-950 text-white shadow-lg sm:rounded-[1.5rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-6 sm:p-8 md:p-10">
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles size={11} style={{ color }} aria-hidden />
              For partners
            </div>
            <h3 className="text-xl font-black leading-snug sm:text-2xl">{data.funnelApplication.conversion.text}</h3>
            <p className="mt-3 text-sm font-bold text-white/85">{data.primaryCtaHeading}</p>
            <Link
              to="/partners/onboard"
              className="mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black"
              style={{ background: color, color: foregroundOnSolidAccent(color) }}
            >
              Become a partner
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
          <div className="relative min-h-[12rem] sm:min-h-[14rem] lg:min-h-0">
            <img src={data.galleryImages[1]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-950/85" />
          </div>
        </div>
      </div>
    </div>
  );
};
